import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSave, FiSearch, FiMapPin } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite } from '../utilities/adminAccess';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';

export default function StadiumsManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();

  const [stadiums, setStadiums] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadStadiums(searchQuery);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, champId, champsLoading]);

  const loadStadiums = async (search) => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const body = {};
      if (search && search.trim()) body.search = search.trim();
      if (champId) body.champ_id = champId;

      const response = await authorizedPost(dispatch, API_URLS.getAllAdminStadiums, body);
      const list = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setStadiums(list);
      setOriginalOrder(list);
      setReorderMode(false);
    } catch (error) {
      setStadiums([]);
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    setMessage({ text: '', type: '' });
    try {
      const ordered = stadiums.map((stadium, index) => ({
        ...stadium,
        priority: index + 1,
      }));
      await authorizedPost(dispatch, API_URLS.changeStadiumsPriority, {
        stadiums: ordered.map((stadium) => ({
          id: stadium.id,
          priority: stadium.priority,
        })),
      });
      setStadiums(ordered);
      setOriginalOrder(ordered);
      setReorderMode(false);
      setMessage({ text: t('stOrderSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stOrderError')), type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: t('stdName'),
      className: 'st-cell-name',
      render: (item) => {
        const address = item.address || '';
        const statusLabel = Number(item.enabled) === 1 ? t('stEnabledShort') : t('stDisabledShort');
        const meta = [statusLabel, address].filter(Boolean).join(' · ');
        return (
          <span className="std-stadium-main">
            <span className="std-stadium-name">{item.name || '—'}</span>
            {meta ? <small className="std-stadium-meta">{meta}</small> : null}
          </span>
        );
      },
    },
    {
      key: 'enabled',
      label: t('stStatus'),
      className: 'st-cell-status',
      render: (item) => (
        <span className={`st-badge ${Number(item.enabled) === 1 ? 'enabled' : 'disabled'}`}>
          <span className="tr-btn-full">{Number(item.enabled) === 1 ? t('stEnabled') : t('stDisabled')}</span>
          <span className="tr-btn-short">{Number(item.enabled) === 1 ? t('stEnabledShort') : t('stDisabledShort')}</span>
        </span>
      ),
    },
    {
      key: 'address',
      label: t('stAddress'),
      className: 'st-cell-address st-cell-muted',
      render: (item) => item.address || '—',
    },
    {
      key: 'priority',
      label: t('stdPriority'),
      className: 'st-cell-priority',
      render: (item, index) => (
        <span className="st-priority-badge">{reorderMode ? index + 1 : (item.priority ?? index + 1)}</span>
      ),
    },
  ];

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page std-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navStadiums') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('stSearchPh')}
              value={searchQuery}
              disabled={reorderMode}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          <div className="st-meta">
            <strong>{t('stFound', { count: stadiums.length })}</strong>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-table-wrap">
            {loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                <p>{t('stLoading')}</p>
              </div>
            ) : (
              <ReorderableTable
                tableClassName="st-table-stadiums"
                columns={columns}
                items={stadiums}
                getRowKey={(item) => item.id}
                reorderMode={reorderMode}
                onItemsChange={setStadiums}
                getRowHref={(item) => (item.id ? `/stadiums_management/${item.id}` : null)}
                empty={(
                  <div className="st-empty">
                    <FiMapPin size={48} />
                    <h3>{t('stEmpty')}</h3>
                    <p>{t('stEmptyHint')}</p>
                  </div>
                )}
              />
            )}
            {reorderMode ? <p className="st-hint">{t('stDragHint')}</p> : null}
          </div>
        </div>

        <div className="st-actions">
          {reorderMode ? (
            <>
              <button
                type="button"
                className="st-btn st-btn-secondary"
                disabled={savingOrder}
                onClick={() => {
                  setStadiums(originalOrder);
                  setReorderMode(false);
                }}
              >
                {t('stCancelReorder')}
              </button>
              <button
                type="button"
                className="st-btn st-btn-primary"
                disabled={savingOrder || !stadiums.length}
                onClick={handleSaveOrder}
              >
                <FiSave /> {savingOrder ? t('stLoading') : t('stSaveOrder')}
              </button>
            </>
          ) : (
            <IfCanWrite entity="stadiums">
              <button
                type="button"
                className="st-btn st-btn-secondary"
                disabled={loading || stadiums.length < 2 || Boolean(searchQuery.trim())}
                onClick={() => {
                  setOriginalOrder(stadiums);
                  setReorderMode(true);
                  setMessage({ text: '', type: '' });
                }}
              >
                <span className="tr-btn-full">{t('stReorder')}</span>
                <span className="tr-btn-short">{t('stReorderShort')}</span>
              </button>
              <Link to="/stadiums_management/new" className="st-btn st-btn-primary">
                <FiPlus />
                <span className="tr-btn-full">{t('stAddStadium')}</span>
                <span className="tr-btn-short">{t('stAddStadiumShort')}</span>
              </Link>
            </IfCanWrite>
          )}
        </div>
      </div>
    </div>
  );
}
